import {useState} from 'react';
import type {
	CompetitorRelationType,
	CompetitorSet,
	CompetitorSetItem,
	OwnCardSuggestion,
} from '@ozon-ai/sellmonitor/contracts';
import {
	COMPETITOR_CONFIDENCE_LABELS,
	COMPETITOR_DISCOVERY_RUN_STATUS_LABELS,
	COMPETITOR_EVIDENCE_SOURCE_LABELS,
	COMPETITOR_MATCH_GROUP_LABELS,
} from '@ozon-ai/sellmonitor/contracts';
import {Alert, AlertDescription} from '@/components/ui/alert.tsx';
import {Badge} from '@/components/ui/badge.tsx';
import {Button} from '@/components/ui/button.tsx';
import {Card, CardContent, CardFooter, CardHeader, CardTitle} from '@/components/ui/card.tsx';
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from '@/components/ui/collapsible.tsx';
import {Empty, EmptyDescription, EmptyHeader, EmptyTitle} from '@/components/ui/empty.tsx';
import {Label} from '@/components/ui/label.tsx';
import {RadioGroup, RadioGroupItem} from '@/components/ui/radio-group.tsx';
import {Skeleton} from '@/components/ui/skeleton.tsx';
import {Spinner} from '@/components/ui/spinner.tsx';
import {useProductPageContext} from './product-context';
import {useCompetitors} from './use-competitors';

function formatWhen(value: string | undefined): string {
	if (!value) return 'ещё не было';
	return new Date(value).toLocaleString('ru-RU', {
		timeZone: 'Europe/Moscow',
		dateStyle: 'medium',
		timeStyle: 'short',
	});
}

type CompetitorFilter = 'all' | 'direct_competitor' | 'close_analogue' | 'excluded' | 'pending_review';

const COMPETITOR_FILTERS: {value: CompetitorFilter; label: string}[] = [
	{value: 'all', label: 'Все'},
	{value: 'direct_competitor', label: 'Прямые конкуренты'},
	{value: 'close_analogue', label: 'Близкие аналоги'},
	{value: 'excluded', label: 'Не конкуренты'},
	{value: 'pending_review', label: 'Не проверены'},
];

function formatPrice(value: number | undefined): string {
	if (value == null) return 'Цена не указана';
	return `${value.toLocaleString('ru-RU', {maximumFractionDigits: 2})} ₽`;
}

export function CompetitorsManagePage() {
	const state = useProductPageContext();
	return (
		<CompetitorsTab
			offerId={state.offerId}
			productName={state.product?.name ?? ''}
			ozonSku={state.product?.ozonSku}
		/>
	);
}

export function CompetitorsTab({
	offerId,
	productName,
	ozonSku,
}: {
	offerId: string;
	productName: string;
	ozonSku?: string;
}) {
	const state = useCompetitors(offerId);
	const [filter, setFilter] = useState<CompetitorFilter>('all');
	const competitorSet = state.competitorSet;
	const mapped = Boolean(competitorSet?.mapping);
	const lastRun = competitorSet?.lastRun;
	const suggestions = state.suggestions.length > 0
		? state.suggestions
		: competitorSet?.ownCardSuggestions ?? [];

	return (
		<div className="flex flex-col gap-4">
			<Card size="sm">
				<CardHeader>
					<CardTitle>{competitorSet?.mapping?.title || productName || competitorSet?.productName || 'Своя карточка'}</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-2">
					<p className="text-sm text-muted-foreground">
						Sellmonitor: {state.oauth == null ? 'проверяем подключение' : state.oauth.connected ? 'подключён' : 'не подключён'}
					</p>
					<p className="text-sm text-muted-foreground">
						Карточка: {mapped ? `подтверждена, ${competitorSet?.mapping?.dwhProductId}` : 'не подтверждена'}
					</p>
					<p className="text-sm text-muted-foreground">
						Последний поиск: {formatWhen(lastRun?.finishedAt)}
						{lastRun ? `, ${COMPETITOR_DISCOVERY_RUN_STATUS_LABELS[lastRun.status]}` : ''}
					</p>
					<p className="text-sm text-muted-foreground">
						Версия правил: {lastRun?.rulesVersion ?? 'появится после поиска'}
					</p>
				</CardContent>
				<CardFooter className="flex flex-wrap gap-2">
					{mapped ? (
						<Button type="button" disabled={state.searching} onClick={() => void state.findCompetitors()}>
							{state.searching ? <Spinner data-icon="inline-start"/> : null}
							{lastRun ? 'Обновить поиск' : 'Найти конкурентов'}
						</Button>
					) : (
						<Button type="button" disabled={state.searching} onClick={() => void state.findOwnCard()}>
							{state.searching ? <Spinner data-icon="inline-start"/> : null}
							Найти карточку Sellmonitor
						</Button>
					)}
				</CardFooter>
			</Card>

			{state.searchNotice ? (
				<Alert>
					<AlertDescription>{state.searchNotice}</AlertDescription>
				</Alert>
			) : null}

			{state.error ? (
				<Alert variant="destructive">
					<AlertDescription>{state.error}</AlertDescription>
				</Alert>
			) : null}

			{lastRun?.warnings.map((warning) => (
				<Alert key={warning}>
					<AlertDescription>{warning}</AlertDescription>
				</Alert>
			))}

			{lastRun?.limitations.map((limitation) => (
				<p key={limitation} className="text-sm text-muted-foreground">{limitation}</p>
			))}

			{state.loading ? <Skeleton className="h-40 w-full"/> : null}

			{!mapped && suggestions.length > 0 ? (
				<section className="flex flex-col gap-3">
					<h2 className="text-base font-medium">Подтвердите свою карточку</h2>
					{suggestions.map((suggestion) => (
						<OwnCardSuggestionCard
							key={suggestion.dwhProductId}
							suggestion={suggestion}
							ozonSku={ozonSku}
							busy={state.busyId === suggestion.dwhProductId}
							onConfirm={() => void state.confirmMapping(suggestion.dwhProductId)}
						/>
					))}
				</section>
			) : null}

			{!state.loading && mapped && competitorSet ? (
				<CandidateGrid
					competitorSet={competitorSet}
					filter={filter}
					onFilterChange={setFilter}
					busyId={state.busyId}
					onClassify={state.classify}
				/>
			) : null}

			{!state.loading && !mapped && suggestions.length === 0 && !state.searching ? (
				<Empty className="border border-border">
					<EmptyHeader>
						<EmptyTitle>Карточка Sellmonitor не подтверждена</EmptyTitle>
						<EmptyDescription>
							Найдите карточку своего товара и подтвердите её. После этого можно искать конкурентов.
						</EmptyDescription>
					</EmptyHeader>
				</Empty>
			) : null}
		</div>
	);
}

function OwnCardSuggestionCard({
	suggestion,
	ozonSku,
	busy,
	onConfirm,
}: {
	suggestion: OwnCardSuggestion;
	ozonSku?: string;
	busy: boolean;
	onConfirm: () => void;
}) {
	const facts = readSuggestionFacts(suggestion);
	const skuMatches = Boolean(ozonSku && facts.sku === ozonSku);
	return (
		<Card size="sm">
			{facts.imageUrl ? (
				<img src={facts.imageUrl} alt="" className="h-40 w-full object-cover"/>
			) : null}
			<CardHeader>
				<CardTitle>{suggestion.title || suggestion.dwhProductId}</CardTitle>
			</CardHeader>
			<CardContent className="flex flex-col gap-2">
				<div className="flex flex-wrap gap-2">
					{facts.sku ? <Badge variant="outline">SKU {facts.sku}</Badge> : null}
					{skuMatches ? <Badge>Совпадает с вашим SKU</Badge> : null}
					{facts.fulfillment ? <Badge variant="outline">{facts.fulfillment.toUpperCase()}</Badge> : null}
				</div>
				<p>{facts.price == null ? 'Цена не указана' : formatPrice(facts.price)}</p>
				<p className="text-sm text-muted-foreground">
					{[facts.seller, facts.category].filter(Boolean).join(' · ') || 'Продавец не указан'}
				</p>
				{facts.rating != null ? (
					<p className="text-sm text-muted-foreground">
						Рейтинг {facts.rating.toLocaleString('ru-RU')}
						{facts.reviews != null ? `, отзывов ${facts.reviews}` : ''}
					</p>
				) : null}
				{facts.link ? (
					<a className="text-sm underline" href={facts.link} target="_blank" rel="noreferrer">
						Открыть на Ozon
					</a>
				) : null}
			</CardContent>
			<CardFooter>
				<Button type="button" disabled={busy} onClick={onConfirm}>
					Это наша карточка
				</Button>
			</CardFooter>
		</Card>
	);
}

function readSuggestionFacts(suggestion: OwnCardSuggestion) {
	const raw = asRecord(suggestion.raw);
	const merchant = asRecord(Array.isArray(raw?.merchants) ? raw.merchants[0] : undefined);
	const categories = Array.isArray(raw?.categories) ? raw.categories : [];
	const category = [...categories].reverse().map(asRecord).find((item) => typeof item?.name === 'string');
	const fulfillment = Array.isArray(raw?.fulfillmentTypes)
		? raw.fulfillmentTypes.filter((item): item is string => typeof item === 'string').join(', ')
		: undefined;
	return {
		imageUrl: suggestion.imageUrl,
		sku: suggestion.marketplaceProductId,
		link: suggestion.link || readString(raw?.url),
		price: typeof raw?.price === 'number' ? raw.price : undefined,
		rating: typeof raw?.rating === 'number' ? raw.rating : undefined,
		reviews: typeof raw?.reviewsTotal === 'number' ? raw.reviewsTotal : undefined,
		seller: readString(merchant?.name),
		category: readString(category?.name),
		fulfillment,
	};
}

function asRecord(value: unknown): Record<string, unknown> | null {
	if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
	return value as Record<string, unknown>;
}

function readString(value: unknown): string | undefined {
	return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function CandidateGrid({
	competitorSet,
	filter,
	onFilterChange,
	busyId,
	onClassify,
}: {
	competitorSet: CompetitorSet;
	filter: CompetitorFilter;
	onFilterChange: (filter: CompetitorFilter) => void;
	busyId: string | null;
	onClassify: (item: CompetitorSetItem, relationType: CompetitorRelationType) => Promise<void>;
}) {
	const items = [
		...competitorSet.directCompetitors,
		...competitorSet.closeAnalogues,
		...competitorSet.pendingReview,
		...competitorSet.excluded,
	];
	const visible = filter === 'all' ? items : items.filter((item) => item.status === filter);
	return (
		<section className="flex flex-col gap-4">
			<RadioGroup
				value={filter}
				onValueChange={onFilterChange}
				aria-label="Какие карточки показать"
				className="flex flex-row flex-wrap gap-x-4 gap-y-2"
			>
				{COMPETITOR_FILTERS.map((option) => (
					<Label key={option.value}>
						<RadioGroupItem value={option.value}/>
						{option.label}
					</Label>
				))}
			</RadioGroup>
			{visible.length === 0 ? (
				<p className="text-sm text-muted-foreground">
					{filter === 'all' ? 'Конкуренты пока не найдены' : 'В этой группе карточек нет'}
				</p>
			) : (
				<div className="grid grid-cols-2 gap-4">
					{visible.map((item) => (
						<CandidateCard
							key={item.dwhProductId}
							item={item}
							busy={busyId === item.dwhProductId}
							onClassify={onClassify}
						/>
					))}
				</div>
			)}
		</section>
	);
}

function CandidateCard({
	item,
	busy,
	onClassify,
}: {
	item: CompetitorSetItem;
	busy: boolean;
	onClassify: (item: CompetitorSetItem, relationType: CompetitorRelationType) => Promise<void>;
}) {
	return (
		<Card size="sm" className="min-w-0">
			{item.imageUrl ? (
				<img
					src={item.imageUrl}
					alt={item.title || "Фото товара"}
					className="h-40 w-full bg-muted object-contain"
				/>
			) : null}
			<CardHeader>
				<CardTitle>{item.title || item.dwhProductId}</CardTitle>
			</CardHeader>
			<CardContent className="flex flex-col gap-2">
				<p>{formatPrice(item.price)}</p>
				<p className="text-sm text-muted-foreground">
					{[item.brand, item.seller].filter(Boolean).join(' · ') || 'Бренд и продавец не указаны'}
				</p>
				<p className="text-sm text-muted-foreground">{item.category || 'Категория не указана'}</p>
				<div className="flex flex-wrap gap-2">
					{item.fulfillment ? <Badge variant="outline">{item.fulfillment}</Badge> : null}
					{item.matchGroup ? (
						<Badge variant="secondary">
							{COMPETITOR_MATCH_GROUP_LABELS[item.matchGroup]}
							{item.matchScore != null ? `, ${item.matchScore}` : ''}
						</Badge>
					) : null}
					{item.confidence ? (
						<Badge variant="outline">Уверенность: {COMPETITOR_CONFIDENCE_LABELS[item.confidence]}</Badge>
					) : null}
					{item.detailsStatus === 'unavailable' ? <Badge variant="outline">Данные неполные</Badge> : null}
				</div>
				{item.changeNote ? (
					<Alert>
						<AlertDescription>{item.changeNote}</AlertDescription>
					</Alert>
				) : null}
				{item.limitations.length > 0 ? (
					<p className="text-sm text-muted-foreground">{item.limitations.join('. ')}</p>
				) : null}
				<Collapsible>
					<CollapsibleTrigger className="text-sm font-medium">Почему этот товар</CollapsibleTrigger>
					<CollapsibleContent className="flex flex-col gap-2 pt-2">
						<p className="text-sm">
							Совпало: {item.matchedFeatures.length > 0 ? item.matchedFeatures.join(', ') : 'нет явных совпадений'}
						</p>
						<p className="text-sm">
							Отличается: {item.mismatchedFeatures.length > 0 ? item.mismatchedFeatures.join(', ') : 'нет'}
						</p>
						{item.evidence.length > 0 ? (
							<ul className="flex flex-col gap-1 text-sm text-muted-foreground">
								{item.evidence.map((evidence) => (
									<li key={evidence.id}>
										{COMPETITOR_EVIDENCE_SOURCE_LABELS[evidence.source]}
										{evidence.query ? `: ${evidence.query}` : ''}
										{evidence.position != null ? `, позиция ${evidence.position}` : ''}
									</li>
								))}
							</ul>
						) : (
							<p className="text-sm text-muted-foreground">Источники появления не сохранены</p>
						)}
					</CollapsibleContent>
				</Collapsible>
			</CardContent>
			<CardFooter className="flex flex-wrap gap-2">
				<RelationButton
					label="Прямой конкурент"
					active={item.status === 'direct_competitor'}
					busy={busy}
					onClick={() => void onClassify(item, 'direct_competitor')}
				/>
				<RelationButton
					label="Близкий аналог"
					active={item.status === 'close_analogue'}
					busy={busy}
					onClick={() => void onClassify(item, 'close_analogue')}
				/>
				<RelationButton
					label="Не конкурент"
					active={item.status === 'excluded'}
					busy={busy}
					onClick={() => void onClassify(item, 'excluded')}
				/>
			</CardFooter>
		</Card>
	);
}

function RelationButton({
	label,
	active,
	busy,
	onClick,
}: {
	label: string;
	active: boolean;
	busy: boolean;
	onClick: () => void;
}) {
	return (
		<Button type="button" size="sm" variant={active ? 'default' : 'outline'} disabled={busy} onClick={onClick}>
			{label}
		</Button>
	);
}
